
import { useState } from 'react';

interface Video {
  id: string;
  title: string;
  description: string | null;
  video_url: string;
  thumbnail_url: string | null;
  category: string | null;
  is_active: boolean;
  display_order: number;
}

interface VideoGalleryProps {
  videos: Video[];
}

export default function VideoGallery({ videos }: VideoGalleryProps) {
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);

  if (!videos || videos.length === 0) {
    return null;
  }

  const getYouTubeId = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const getYouTubeThumbnail = (url: string) => {
    const videoId = getYouTubeId(url);
    return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Video Galeri
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Çalışmalarımızdan video örnekleri
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((video) => {
            const thumbnail = video.thumbnail_url || getYouTubeThumbnail(video.video_url);
            
            return (
              <div
                key={video.id}
                onClick={() => setSelectedVideo(video)}
                className="relative group cursor-pointer rounded-lg overflow-hidden shadow-lg border border-gray-100"
              >
                <div className="w-full h-64 bg-gray-200">
                  {thumbnail ? (
                    <img
                      src={thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-800">
                      <i className="ri-video-line text-4xl text-gray-400"></i>
                    </div>
                  )}
                </div>
                
                {/* Oynat Butonu */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors">
                  <div className="w-16 h-16 flex items-center justify-center bg-[#FCD34D] rounded-full group-hover:scale-110 transition-transform">
                    <i className="ri-play-fill text-3xl text-black ml-1"></i>
                  </div>
                </div>

                {/* Başlık */}
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                  <h3 className="text-white font-semibold">{video.title}</h3>
                  {video.category && (
                    <span className="text-sm text-gray-300">{video.category}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedVideo(null)}
        >
          <div 
            className="relative w-full max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedVideo(null)}
              className="absolute -top-12 right-0 w-10 h-10 flex items-center justify-center text-white hover:text-[#FCD34D] transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-3xl"></i>
            </button>
            
            <div className="aspect-video bg-black rounded-lg overflow-hidden">
              {getYouTubeId(selectedVideo.video_url) ? (
                <iframe
                  src={`https://www.youtube.com/embed/${getYouTubeId(selectedVideo.video_url)}?autoplay=1`}
                  title={selectedVideo.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              ) : (
                <video
                  src={selectedVideo.video_url}
                  controls
                  autoPlay
                  className="w-full h-full"
                >
                  Tarayıcınız video etiketini desteklemiyor.
                </video>
              )}
            </div>

            <div className="mt-4">
              <h3 className="text-xl font-bold text-white">{selectedVideo.title}</h3>
              {selectedVideo.description && (
                <p className="text-gray-400 mt-2">{selectedVideo.description}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
